import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import back_app from '../../lib/back_app';
import CreatorHeader from '../../components/creator/CreatorHeader';

export default function PublishSocial() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vcamp_id = searchParams.get('id');

  const [vcamp, set_vcamp] = useState<any>(null);
  const [loading, set_loading] = useState(true);
  const [submitting, set_submitting] = useState(false);
  const [url, set_url] = useState('');
  const [err, set_err] = useState<string | null>(null);
  const [show_success, set_show_success] = useState(false);
  const [downloading, set_downloading] = useState(false);
  const [download_err, set_download_err] = useState<string | null>(null);

  useEffect(() => {
    if (!vcamp_id)
      return;
    (async () => {
      try {
        let res = await back_app.vcamp_get(vcamp_id);
        if (!res?.err) {
          set_vcamp(res);
          if (res.published_url)
            set_url(res.published_url);
        }
      } catch (e: any) {
        if (e?.message == 'no session')
          navigate('/login');
      } finally {
        set_loading(false);
      }
    })();
  }, [vcamp_id]);

  const handle_download = async () => {
    if (!vcamp?.processed_video_url)
      return;
    set_download_err(null);
    set_downloading(true);
    try {
      let res = await fetch(vcamp.processed_video_url);
      if (!res.ok)
        throw new Error('http_'+res.status);
      let blob = await res.blob();
      let blob_url = URL.createObjectURL(blob);
      let a = document.createElement('a');
      a.href = blob_url;
      a.download = `sponly_${vcamp.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blob_url), 1000);
    } catch (e: any) {
      set_download_err(e?.message || 'download failed');
      window.open(vcamp.processed_video_url, '_blank');
    } finally {
      set_downloading(false);
    }
  };

  const is_final = vcamp?.published_status == 'approved'
    || vcamp?.published_status == 'rejected'
    || vcamp?.published_status == 'pending_review';

  const handle_submit = async () => {
    set_err(null);
    if (!/^https?:\/\//u.test(url)) {
      set_err('Please enter a valid URL starting with http(s)://');
      return;
    }
    set_submitting(true);
    try {
      let res = await back_app.vcamp_publish({id: vcamp_id!, url});
      if (res?.err) {
        const err_map: Record<string, string> = {
          campaign_not_active: 'This campaign is no longer active. Payment was not processed.',
          campaign_account_not_found: 'Brand has not funded this campaign yet. Try again later.',
          insufficient_funds: 'Brand campaign is out of funds. Payment was not processed.',
          no_payment_for_tier: 'No payment is configured for your creator tier on this campaign.',
          invalid_url: 'Please enter a valid post URL.',
          platform_not_supported: 'Only TikTok and YouTube URLs are supported.',
          platform_disabled: 'Instagram and Facebook are not supported yet. Please publish on TikTok or YouTube.',
          not_video: 'This URL is not a public video post. Paste the link to the video itself, not a profile.',
          verify_unavailable: 'Could not verify the video right now. Please try again later.',
          video_not_ready: 'Your video is still processing.',
          already_approved: 'This video was already approved.',
          already_rejected: 'Tracking for this video has ended.',
          already_pending_review: 'This video was already submitted.',
        };
        set_err(err_map[res.err] || res.err);
        return;
      }
      set_vcamp({...vcamp, published_url: url, published_status: res.published_status,
        track_until: res.track_until});
      set_show_success(true);
    } catch (e: any) {
      set_err(e?.message || 'failed');
    } finally {
      set_submitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!vcamp || !vcamp.processed_video_url) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Processed video not ready
          </h2>
          <button
            onClick={() => navigate('/creator/videos')}
            className="text-blue-600 hover:underline"
          >
            Back to videos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <span>←</span>
          <span>Back</span>
        </button>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
          Publish to Social
        </h1>

        <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center flex-shrink-0">1</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-1">Download your video</h3>
              <p className="text-sm text-gray-600 mb-4">
                Save the processed video to your device.
              </p>
              <button
                onClick={handle_download}
                disabled={downloading}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-60"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                </svg>
                {downloading ? 'Downloading...' : 'Download Video'}
              </button>
              {download_err && (
                <p className="text-red-600 text-sm mt-3">
                  {download_err} - opened the video in a new tab as a fallback;
                  right-click to save.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center flex-shrink-0">2</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-1">Upload to your social platform</h3>
              <p className="text-sm text-gray-600">
                Open your social platform (YouTube or TikTok),
                upload the downloaded video, and publish it. Once it's live, copy
                the post URL — you'll need it in the next step.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center flex-shrink-0">3</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-1">Submit the published URL</h3>
              <p className="text-sm text-gray-600 mb-4">
                Paste the URL of the live post. We'll verify it's a video, then check
                its views daily for 14 days. Payment is released once it reaches
                1,000 views.
              </p>
              <input
                type="url"
                value={url}
                onChange={(e) => set_url(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full px-4 py-3 border rounded-lg mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {err && <p className="text-red-600 text-sm mb-3">{err}</p>}
              <button
                onClick={handle_submit}
                disabled={!url || submitting || is_final}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-60"
              >
                {submitting ? 'Submitting...' : 'Submit for verification'}
              </button>
              {vcamp.published_status == 'pending_review' && (
                <p className="text-sm text-yellow-700 mt-3">
                  Pending verification.
                </p>
              )}
              {vcamp.published_status == 'tracking' && (
                <p className="text-sm text-blue-700 mt-3">
                  Tracking views: {(vcamp.views?.cnt || 0).toLocaleString()} / 1,000
                  {vcamp.track_until && ` until ${new Date(vcamp.track_until).toLocaleDateString()}`}
                </p>
              )}
              {vcamp.published_status == 'approved' && (
                <p className="text-sm text-green-700 mt-3">
                  Approved - paid ${(vcamp.payment_amount || 0).toLocaleString()}.
                </p>
              )}
              {vcamp.published_status == 'rejected' && (
                <p className="text-sm text-red-700 mt-3">
                  The video didn't reach 1,000 views within 14 days.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {show_success && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">URL submitted</h2>
            <p className="text-gray-600 mb-6">
              We'll check your post's views daily. Payment is released once it
              reaches 1,000 views within 14 days.
            </p>
            <button
              onClick={() => navigate('/creator/my-campaigns')}
              className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              Go to My Campaigns
            </button>
          </div>
        </div>
      )}
    </div>
  );
}